/* @flow */
import React from 'react';
import type { GlyphName } from '@remind/r101-glyphs';
import GroupInviteModal from 'groups/components/GroupInviteModal.jsx';
import Group from 'entities/Group';
import Avatar from 'components/Avatar.jsx';
import { mountModal } from 'lib/modal';
import ActivityCreateDialogContainer from 'payments/containers/ActivityCreateDialogContainer.jsx';
import GroupMessageListModal from 'groups/components/GroupMessageListModal.jsx';
import UniversalComposer, { PANELS } from 'chat/components/UniversalComposer.jsx';
import CurrentUser from 'auth/CurrentUser';
import { browserHistory } from 'react-router';
import RelationshipActions from 'actions/RelationshipActions';
import Relationship from 'entities/Relationship';
import RelationshipStore from 'stores/RelationshipStore';
import { QuickActionUIDs } from 'entities/QuickActions';
import type { QuickActionType } from 'entities/QuickActions';
import EventTracking from 'lib/EventTracking';

import { Container } from 'flux/utils';

import './styles/QuickActionsBarItem.scss';

type Props = {
  action: QuickActionType,
  group: Group,
  visible: boolean,
};

type State = {
  relationship?: ?Relationship,
};

type Action = {
  title: string,
  glyph: GlyphName,
  action: () => void,
};

class QuickActionsBarItem extends React.Component {
  props: Props;
  state: State;

  static contextTypes = {
    eventScreenName: React.PropTypes.string,
  };

  static getStores() {
    return [RelationshipStore];
  }

  static calculateState(prevState: ?State, props: Props) {
    let relationship = null;
    if (props.action.uid === QuickActionUIDs.PERSON && props.action.data && !props.action.data.chat_uuid) {
      const { id } = props.action.data.user;
      if (id) {
        relationship = RelationshipStore.getRelationshipWithUser(id);
      }
    }
    return { relationship };
  }

  componentWillReceiveProps(props: Props) {
    if (!props.visible || this.props.visible || this.state.relationship || this.props.action.uid !== QuickActionUIDs.PERSON) {
      return;
    }
    if (this.props.action.data && this.props.action.data.chat_uuid) {
      return;
    }

    const id = this.props.action.data && this.props.action.data.user.id;
    if (id && !RelationshipStore.getRelationshipWithUser(id)) {
      RelationshipActions.loadRelationship(id);
    }
  }

  withTracking(type: string, fn: Function): Function {
    return () => {
      EventTracking.tap(
        this.context.eventScreenName,
        `QuickActionsBarItem-${type}`,
        null,
        {},
      );
      fn();
    };
  }

  actionDetails: {[key: string]: Action} = {
    [QuickActionUIDs.ADD_PEOPLE]: {
      title: 'Add people',
      glyph: 'avatar-add',
      action: this.withTracking(QuickActionUIDs.ADD_PEOPLE, () => {
        mountModal(<GroupInviteModal group={this.props.group} />, this);
      }),
    },
    [QuickActionUIDs.CREATE_GROUP_CHAT]: {
      title: 'Group message',
      glyph: 'message-group',
      action: this.withTracking(QuickActionUIDs.CREATE_GROUP_CHAT, () => {
        mountModal(
          <UniversalComposer
            canAnnounce={CurrentUser.getUser().canComposeAnnouncements()}
            panel={PANELS.GROUP_CONVERSATION}
          />,
          this
        );
      }),
    },
    [QuickActionUIDs.SHOW_HISTORY]: {
      title: 'Announcements',
      glyph: 'megaphone',
      action: this.withTracking(QuickActionUIDs.SHOW_HISTORY, () => {
        mountModal(<GroupMessageListModal groupCode={this.props.group.getCode()} />, this);
      }),
    },
    [QuickActionUIDs.CREATE_ACTIVITY]: {
      title: 'Activity',
      glyph: 'calendar-stroke',
      action: this.withTracking(QuickActionUIDs.CREATE_ACTIVITY, () => {
        mountModal(<ActivityCreateDialogContainer />, this);
      }),
    },
  };

  navigateToMember = this.withTracking(QuickActionUIDs.PERSON, () => {
    const data = this.props.action.data;
    const uuid = data && data.chat_uuid;
    if (uuid) {
      browserHistory.push(`/classes/${this.props.group.getCode()}/messages/${uuid}`);
    } else {
      const relationship = this.state.relationship;

      if (relationship) {
        browserHistory.push(`/classes/${this.props.group.getCode()}/messages/new/${relationship.id}`);
      }
    }
  });

  render() {
    switch (this.props.action.uid) {
      case QuickActionUIDs.DIVIDER: {
        return <div className="QuickActionsBarItem QuickActionsBarItem-divider" />;
      }
      case QuickActionUIDs.PERSON: {
        if (!this.props.action.data) {
          return <div />;
        }
        return (
          <div
            className="QuickActionsBarItem QuickActionsBarItem-person"
            onClick={this.navigateToMember}>
            <Avatar
              colorHex={this.props.action.data.user.color}
              text={this.props.action.data.user.initials}
              size="medium"
            />
            <div className="QuickActionsBarItem-title">
              {this.props.action.data.user.name}
            </div>
          </div>
        );
      }
      default: {
        const details = this.actionDetails[this.props.action.uid];
        if (!details) {
          return <div />;
        }
        return (
          <div
            className="QuickActionsBarItem QuickActionsBarItem-action"
            onClick={details.action}>
            <Avatar
              color="brand"
              glyphName={details.glyph}
              size="medium"
            />
            <div className="QuickActionsBarItem-title">
              {details.title}
            </div>
          </div>
        );
      }
    }
  }
}

export default Container.create(QuickActionsBarItem, { withProps: true });

export function widthForAction(action: QuickActionType): number {
  switch (action.uid) {
    case QuickActionUIDs.DIVIDER:
      return 11;
    case QuickActionUIDs.PERSON:
      return 98;
    default:
      return 98;
  }
}



// WEBPACK FOOTER //
// ./src/chat/components/QuickActionsBarItem.jsx